import { useState, useMemo } from "react";
import { Mail, Send, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

/**
 * EmailLocatoreModal — from the admin SubmissionDetail page, the admin can
 * compose a custom message addressed to the locatore's email. The message is
 * sent via /api/admin/submissions/{id}/email-locatore and appended to
 * submission.admin_emails_sent for history.
 */
export default function EmailLocatoreModal({ open, onOpenChange, submission, onSent }) {
  const loc = submission?.data?.locatore || {};
  const locName = loc.nome || "";
  const locEmail = (loc.email || "").trim();
  const hasEmail = /@/.test(locEmail);

  const defaultSubject = useMemo(
    () => `Pratica Canone Concordato — richiesta di modifiche`,
    [],
  );
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(
    `Gentile ${locName || "locatore"},\n\nla ringraziamo per la richiesta di attestazione inviata a UnionCasa Milano.\nVerificando la pratica abbiamo riscontrato quanto segue:\n\n- \n- \n\nLe chiediamo di apportare le modifiche indicate accedendo alla sua area riservata o rispondendo a questa email.\n\nRestiamo a disposizione,\nUnionCasa Milano`,
  );
  const [sending, setSending] = useState(false);

  const reset = () => {
    setSubject(defaultSubject);
    setBody("");
  };

  const handleSend = async () => {
    if (!hasEmail) {
      toast.error("Il locatore non ha un'email valida nella pratica");
      return;
    }
    if (!subject.trim()) {
      toast.error("Inserisci un oggetto");
      return;
    }
    if (!body.trim()) {
      toast.error("Scrivi il messaggio prima di inviare");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/admin/submissions/${submission.id}/email-locatore`, {
        subject: subject.trim(),
        body: body.trim(),
      });
      toast.success(`Email inviata a ${r.data.recipient}`);
      onSent?.(r.data);
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="email-locatore-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail size={18} className="text-slate-700" />
            Invia email al/alla Locatore/Locatrice
          </DialogTitle>
          <DialogDescription>
            Scrivi una comunicazione diretta al locatore per segnalare modifiche o richieste sulla pratica.
            L&apos;email verrà inviata da UnionCasa Milano.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Destinatario</span>
            <span className="text-slate-900 font-medium" data-testid="email-loc-recipient-name">{locName || "Locatore"}</span>
            {hasEmail ? (
              <span className="text-slate-600" data-testid="email-loc-recipient-address">&lt;{locEmail}&gt;</span>
            ) : (
              <span className="text-red-700 font-semibold">Email non presente nella pratica</span>
            )}
          </div>

          <div>
            <Label htmlFor="email-loc-subject">Oggetto</Label>
            <Input
              id="email-loc-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1.5"
              data-testid="email-loc-subject"
              disabled={sending}
              placeholder="Es. Pratica Canone Concordato — richiesta di modifiche"
            />
          </div>

          <div>
            <Label htmlFor="email-loc-body">Messaggio</Label>
            <textarea
              id="email-loc-body"
              rows={12}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
              data-testid="email-loc-body"
              disabled={sending}
              placeholder="Scrivi qui il messaggio…"
            />
            <p className="text-xs text-slate-400 mt-1">
              Il messaggio viene inserito in un template UnionCasa con logo e footer. Le interruzioni di riga sono preservate.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="email-loc-cancel"
          >
            <X size={14} className="mr-1.5" />
            Annulla
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !hasEmail}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="email-loc-send"
          >
            <Send size={14} className="mr-1.5" />
            {sending ? "Invio in corso…" : "Invia email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
